import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import {
  Lock,
  Users,
  Mail,
  FileText,
  Download,
  RefreshCw,
  Briefcase,
  MessageSquare,
  Calendar,
  Phone,
  Building,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [applications, setApplications] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/login`, { password });
      setIsAuthenticated(true);
      toast.success("Welcome to Admin Dashboard");
      fetchData();
    } catch (error) {
      toast.error("Incorrect password");
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [appsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/careers/applications`),
        axios.get(`${API}/contact`),
      ]);
      setApplications(appsRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-AU", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-[#0B1C3E] flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-[#0B1C3E] font-['Outfit']">
                Admin Dashboard
              </h1>
              <p className="text-slate-600 mt-2">
                Enter password to access the dashboard
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <Label htmlFor="password" className="text-[#0B1C3E] font-medium">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 h-12 border-slate-200"
                  placeholder="Enter admin password"
                  data-testid="admin-password-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#0B1C3E] hover:bg-[#0B1C3E]/90 text-white rounded-full py-6 font-semibold"
                data-testid="admin-login-btn"
              >
                Access Dashboard
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0B1C3E] font-['Outfit']">
              Admin Dashboard
            </h1>
            <p className="text-slate-600 mt-1">
              Manage job applications and contact enquiries
            </p>
          </div>
          <Button
            onClick={fetchData}
            disabled={loading}
            className="bg-[#2A9D8F] hover:bg-[#2A9D8F]/90 text-white rounded-full"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 border border-slate-100 shadow-sm">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded-lg bg-[#E86C3F]/10 flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-[#E86C3F]" />
              </div>
              <div>
                <p className="text-slate-600 text-sm">Job Applications</p>
                <p className="text-3xl font-bold text-[#0B1C3E] font-['Outfit']">
                  {applications.length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-6 border border-slate-100 shadow-sm">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded-lg bg-[#2A9D8F]/10 flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-[#2A9D8F]" />
              </div>
              <div>
                <p className="text-slate-600 text-sm">Contact Enquiries</p>
                <p className="text-3xl font-bold text-[#0B1C3E] font-['Outfit']">
                  {contacts.length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="applications" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1 rounded-lg">
            <TabsTrigger
              value="applications"
              className="data-[state=active]:bg-[#0B1C3E] data-[state=active]:text-white rounded-md px-6"
            >
              <Briefcase className="w-4 h-4 mr-2" />
              Job Applications
            </TabsTrigger>
            <TabsTrigger
              value="contacts"
              className="data-[state=active]:bg-[#0B1C3E] data-[state=active]:text-white rounded-md px-6"
            >
              <Mail className="w-4 h-4 mr-2" />
              Contact Enquiries
            </TabsTrigger>
          </TabsList>

          {/* Applications Tab */}
          <TabsContent value="applications">
            <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold text-[#0B1C3E]">Name</TableHead>
                      <TableHead className="font-semibold text-[#0B1C3E]">Email</TableHead>
                      <TableHead className="font-semibold text-[#0B1C3E]">Phone</TableHead>
                      <TableHead className="font-semibold text-[#0B1C3E]">Position</TableHead>
                      <TableHead className="font-semibold text-[#0B1C3E]">Experience</TableHead>
                      <TableHead className="font-semibold text-[#0B1C3E]">Resume</TableHead>
                      <TableHead className="font-semibold text-[#0B1C3E]">Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {applications.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                          <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                          No applications yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      applications.map((app, index) => (
                        <TableRow key={app.id || index} className="hover:bg-slate-50">
                          <TableCell className="font-medium text-[#0B1C3E]">
                            {app.name}
                          </TableCell>
                          <TableCell>
                            <a
                              href={`mailto:${app.email}`}
                              className="text-[#2A9D8F] hover:underline"
                            >
                              {app.email}
                            </a>
                          </TableCell>
                          <TableCell className="text-slate-600">
                            {app.phone || "-"}
                          </TableCell>
                          <TableCell>
                            <span className="px-2 py-1 bg-[#5D6D35]/10 text-[#5D6D35] rounded-full text-xs font-medium">
                              {app.position || "Not specified"}
                            </span>
                          </TableCell>
                          <TableCell className="text-slate-600">
                            {app.experience || "-"}
                          </TableCell>
                          <TableCell>
                            {app.resume_filename ? (
                              <span className="flex items-center text-[#2A9D8F]">
                                <FileText className="w-4 h-4 mr-1" />
                                {app.resume_filename}
                              </span>
                            ) : (
                              <span className="text-slate-400">No file</span>
                            )}
                          </TableCell>
                          <TableCell className="text-slate-600 text-sm">
                            {formatDate(app.created_at)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold text-[#0B1C3E]">Name</TableHead>
                      <TableHead className="font-semibold text-[#0B1C3E]">Email</TableHead>
                      <TableHead className="font-semibold text-[#0B1C3E]">Phone</TableHead>
                      <TableHead className="font-semibold text-[#0B1C3E]">Company</TableHead>
                      <TableHead className="font-semibold text-[#0B1C3E]">Message</TableHead>
                      <TableHead className="font-semibold text-[#0B1C3E]">Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contacts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                          <Mail className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                          No enquiries yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      contacts.map((contact, index) => (
                        <TableRow key={contact.id || index} className="hover:bg-slate-50">
                          <TableCell className="font-medium text-[#0B1C3E]">
                            {contact.name}
                          </TableCell>
                          <TableCell>
                            <a
                              href={`mailto:${contact.email}`}
                              className="text-[#2A9D8F] hover:underline"
                            >
                              {contact.email}
                            </a>
                          </TableCell>
                          <TableCell className="text-slate-600">
                            {contact.phone || "-"}
                          </TableCell>
                          <TableCell className="text-slate-600">
                            {contact.company || "-"}
                          </TableCell>
                          <TableCell className="max-w-xs">
                            <p className="text-slate-600 truncate" title={contact.message}>
                              {contact.message}
                            </p>
                          </TableCell>
                          <TableCell className="text-slate-600 text-sm">
                            {formatDate(contact.created_at)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
